import React, { useState } from 'react';
import { DocumentUploadForm, DEFAULT_ACCEPTED_FILE_TYPES, DEFAULT_MAX_FILE_SIZE_MB } from './DocumentUploadForm';
import { Card } from './Card';
import { Modal } from './Modal';
import { Stack } from './Stack';
import { Text } from './Text';
import { Toast } from './Toast';

interface DocumentUploadProps {
    documentTypes: string | string[];
    button: React.ReactElement;
    onSubmit: (file: File, typeName: string) => Promise<void>;
    successMessage?: string;
    errorMessage?: string;
    acceptedFileTypes?: string;
    maxFileSizeMB?: number;
    selectTitle?: string;
    uploadTitle?: string;
    description?: string;
}

export function DocumentUpload({
    documentTypes,
    button,
    onSubmit,
    successMessage = 'Document uploaded',
    errorMessage = 'Could not upload document',
    acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
    maxFileSizeMB = DEFAULT_MAX_FILE_SIZE_MB,
    selectTitle = 'Select Document Type',
    uploadTitle,
    description,
}: DocumentUploadProps) {
    const types = Array.isArray(documentTypes) ? documentTypes : [documentTypes];
    const isSingle = !Array.isArray(documentTypes);
    const [open, setOpen] = useState(false);
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [toast, setToast] = useState<string | null>(null);

    function showToast(message: string) {
        setToast(message);
        setTimeout(() => setToast(null), 3000);
    }

    function openModal() {
        setOpen(true);
        setSelectedType(isSingle ? types[0] : null);
    }

    function closeModal() {
        setOpen(false);
        setSelectedType(null);
    }

    async function handleSubmit(file: File): Promise<boolean> {
        if (!selectedType) return false;
        try {
            await onSubmit(file, selectedType);
        } catch {
            showToast(errorMessage);
            return false;
        }
        closeModal();
        showToast(successMessage);
        return true;
    }

    const trigger = React.cloneElement(button, {
        onClick: (e: React.MouseEvent) => {
            button.props.onClick?.(e);
            openModal();
        },
    });

    return (
        <>
            {trigger}
            {open && !selectedType && (
                <Modal title={selectTitle} onClose={closeModal} width={520}>
                    <Stack gap="sm">
                        {types.map((type) => (
                            <Card key={type} padding="xs" onClick={() => setSelectedType(type)}>
                                <Text weight="semibold">{type}</Text>
                            </Card>
                        ))}
                    </Stack>
                </Modal>
            )}
            {open && selectedType && (
                <Modal title={uploadTitle ?? `Upload ${selectedType}`} onClose={closeModal} width={560}>
                    <DocumentUploadForm
                        selectedType={selectedType}
                        onChangeType={isSingle ? null : () => setSelectedType(null)}
                        onCancel={closeModal}
                        onSubmit={handleSubmit}
                        acceptedFileTypes={acceptedFileTypes}
                        maxFileSizeMB={maxFileSizeMB}
                        description={description}
                    />
                </Modal>
            )}
            <Toast message={toast} />
        </>
    );
}
